import React, {useCallback, useEffect, useState} from 'react';
import {Modal, Button, Radio, Input, Space} from 'antd';
import {ModelDTO} from 'hemwb-api/dist/models/ModelDTO';
import {Form, useForm} from '../../../../core/Form';
import {AbstractControl, FormControl, FormGroup, ValidatorFn, Validators} from 'react-reactive-form';
import {requestSendModelAccessRequested} from '../../../../../store/model';
import {trackModelAccessRequested} from '../../../../../tracking/tracking';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {useDispatch} from 'react-redux';
import {tid} from '../../../../../testUtils';
import {useMounted} from '../../../../../hooks/useMounted';

type ModelRequestAccessDialogProps = {
  model: ModelDTO;
  open: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
};

const reasonOptions = [
  'To develop a local model and/or materials for external use (e.g., HTA submissions)',
  'To create materials for internal use (e.g., training resources)',
  'To inform a strategic policy, resource, or commercial decision',
  'Other',
];

const reasonTextValidator: ValidatorFn = (control: AbstractControl) => {
  return !control.value && control.parent?.get('reason')?.value === 'Other' ? {required: true} : null;
};

const ModelRequestAccessDialog: React.FC<ModelRequestAccessDialogProps> = ({model, open, onSuccess, onCancel}) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const mounted = useMounted();

  const form = useForm(
    new FormGroup({
      reason: new FormControl(null, Validators.required),
      reasonText: new FormControl(null, [reasonTextValidator]),
    }),
  );

  useEffect(() => {
    const updateReasonTextValidity = () => form.get('reasonText').updateValueAndValidity();
    form.get('reason').valueChanges.subscribe(updateReasonTextValidity);
    return () => {
      form.get('reason').valueChanges.unsubscribe(updateReasonTextValidity);
    };
  }, [form]);

  // Track form validity changes
  useEffect(() => {
    const handleStatusChange = () => {
      setIsFormValid(form.valid);
    };
    form.statusChanges.subscribe(handleStatusChange);
    return () => {
      form.statusChanges.unsubscribe(handleStatusChange);
    };
  }, [form]);

  const handleSubmit = useCallback(() => {
    if (!form.valid) {
      return;
    }

    setSubmitting(true);
    const value = form.value;

    const reason = [value.reason, value.reasonText].filter(Boolean).join('<br />');

    requestSendModelAccessRequested(model.uuid, reason)
      .then(() => {
        trackModelAccessRequested(model, value.reason, value.reasonText);
        dispatch(
          messageAdd(
            'Model access request email has been sent. You will get an email notification once the model access is granted.',
            MessageTypes.SUCCESS,
          ),
        );
        onSuccess?.();
      })
      .catch(() => {
        dispatch(messageAdd('Failed to request access', MessageTypes.ERROR));
      })
      .finally(() => {
        mounted.current && setSubmitting(false);
      });
  }, [dispatch, mounted, form, model, onSuccess]);

  return (
    <Modal
      width={594}
      open={open}
      title="Request Access"
      onCancel={onCancel}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={onCancel} {...tid('dialog-request-access', 'cancel')}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          disabled={!isFormValid}
          {...tid('dialog-request-access', 'submit')}
        >
          Request Access
        </Button>,
      ]}
      {...tid('dialog-request-access')}
    >
      <Form
        group={form}
        onSubmit={handleSubmit}
        render={({get}) => {
          const reasonControl = get('reason');
          const reasonTextControl = get('reasonText');
          const showError = reasonControl.touched && !reasonControl.valid;
          const showReasonTextError = reasonTextControl.touched && !reasonTextControl.valid;

          return (
            <Space direction="vertical" size="large" style={{width: '100%'}}>
              <div style={{display: 'block', position: 'relative', top: '10px'}}>
                Reason<span style={{color: '#ff4d4f'}}> *</span>
              </div>
              <Radio.Group
                value={reasonControl.value}
                onChange={(e) => {
                  reasonControl.setValue(e.target.value);
                  reasonControl.markAsTouched();
                  reasonControl.updateValueAndValidity();
                }}
                style={{width: '100%'}}
              >
                <Space direction="vertical" style={{width: '100%'}}>
                  {reasonOptions.map((option) => (
                    <Radio key={option} value={option} {...tid('radio-option', option)}>
                      {option}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
              {showError && (
                <div style={{color: '#ff4d4f', fontSize: '14px', marginTop: '4px'}}>Please select a reason</div>
              )}

              <div>
                <div style={{display: 'block', marginBottom: '8px', fontWeight: 500}}>
                  Please share more details about your request.
                  {reasonControl.value === 'Other' && <span style={{color: '#ff4d4f'}}> *</span>}
                </div>
                <Input.TextArea
                  rows={2}
                  value={reasonTextControl.value || ''}
                  onChange={(e) => {
                    reasonTextControl.setValue(e.target.value);
                    reasonTextControl.markAsTouched();
                    reasonTextControl.updateValueAndValidity();
                  }}
                  placeholder="Textarea"
                  status={showReasonTextError ? 'error' : undefined}
                />
                {showReasonTextError && (
                  <div style={{color: '#ff4d4f', fontSize: '14px', marginTop: '4px'}}>This field is required.</div>
                )}
              </div>
            </Space>
          );
        }}
      />
    </Modal>
  );
};

export default ModelRequestAccessDialog;
